import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { fetchFeatureFlags, featuresFlags } from "@/lib/hey/core";
import { useDevicePlatform } from "@/utils/device";
import { useIsMobile } from "@/hooks/use-mobile";

export type featureFlagStatus = {
  googleAds: boolean;
  conversationalAssistant: boolean;
};

const FeatureFlagContext = createContext<featureFlagStatus | undefined>(undefined);

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [featureFlagStatus, setFeatureFlagStatus] = useState<featureFlagStatus>({
    googleAds: false,
    conversationalAssistant: false,
  });
  const lastEmittedRef = useRef<featureFlagStatus>({
    googleAds: false,
    conversationalAssistant: false,
  });
  const { platform } = useDevicePlatform();
  const isMobile = useIsMobile();
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    async function fetchFlagStatus(): Promise<void> {
      try {
        const flag = await fetchFeatureFlags();
        const parsed = parseFlag(flag);
        if (!isMountedRef.current) return;
        const isChanged =
          lastEmittedRef.current.googleAds !== parsed.googleAds ||
          lastEmittedRef.current.conversationalAssistant !== parsed.conversationalAssistant;
        if (isChanged) {
          console.log('[FeatureFlagProvider] Company time:', new Date().toISOString(), 'Feature flag changed:', parsed);
          lastEmittedRef.current = parsed;
          setFeatureFlagStatus(parsed);
        } else { 
          console.debug('[FeatureFlagProvider] No change in feature flag status, skipping update.');
        }
      } catch (e) {
        // Optionally handle errors here
      }
    }
    function parseFlag(flag: featuresFlags | null): featureFlagStatus {
      const googleAdsVal = typeof flag?.googleAds === 'string' ? flag.googleAds.toLowerCase() : flag?.googleAds;
      const convVal = typeof flag?.conversationalAssistant === 'string' ? flag.conversationalAssistant.toLowerCase() : flag?.conversationalAssistant;
      return {
        googleAds: (googleAdsVal === 'y' || googleAdsVal === 'true') && isValidToDisplay(),
        conversationalAssistant: convVal === 'y' || convVal === 'true',
      };
    }


    // Check if the feature flag is valid to display based on platform and device type
    // This is a placeholder function, you can modify the logic as needed
    // For example, you might want to disable Google Ads on certain platforms or devices
    function isValidToDisplay(): boolean {  
      let isValid = true;
      if(isMobile) {
        if (platform === 'apple') {
          isValid = false; // Disable Google Ads on Apple devices
        }
      }
      return isValid;
    }

    fetchFlagStatus();
    const timer = window.setInterval(fetchFlagStatus, 10000);
    return () => {
      isMountedRef.current = false;
      clearInterval(timer);
    };
  }, [platform, isMobile]);

  return (
    <FeatureFlagContext.Provider value={featureFlagStatus}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export function useFeatureFlagStatus(): featureFlagStatus {
  const ctx = useContext(FeatureFlagContext);
  if (!ctx) {
    throw new Error("useFeatureFlagStatus must be used within a FeatureFlagProvider");
  }
  return ctx;
}
